import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field, Th, Td, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2, RefreshCw, Loader2, TrendingUp } from "lucide-react";
import DateField from "@/components/DateField";

const TYPES = ["stock","mutual_fund","etf","crypto","bond","real_estate","gold","other"];

export default function Investments() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name:"", type:"stock", invested_amount:"", current_value:"", quantity:"", date:new Date().toISOString().slice(0,10), symbol:"" });
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get("/investments"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEdit(null); setForm({ name:"", type:"stock", invested_amount:"", current_value:"", quantity:"", date:new Date().toISOString().slice(0,10), symbol:"" }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ ...i, quantity: i.quantity ?? "", symbol: i.symbol ?? "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      invested_amount: parseFloat(form.invested_amount),
      current_value: parseFloat(form.current_value),
      quantity: form.quantity === "" ? null : parseFloat(form.quantity),
      symbol: form.symbol?.trim() ? form.symbol.trim().toUpperCase() : null,
    };
    try {
      if (edit) await api.put(`/investments/${edit.id}`, payload); else await api.post("/investments", payload);
      toast.success(t("common.toast_saved")); setOpen(false); load();
    } catch { toast.error(t("common.toast_failed")); }
  };
  const remove = async (id) => { if (!confirm("Delete investment?")) return; await api.delete(`/investments/${id}`); toast.success(t("common.toast_deleted")); load(); };

  // Alpha Vantage price refresh — hits /api/investments/refresh-prices which
  // is rate-limited server-side (5 req/min). Free tier caps at 6 symbols per
  // click; the toast surfaces skipped counts.
  const refreshPrices = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/investments/refresh-prices");
      if (data.updated > 0) {
        const cacheBits = data.cached_used > 0
          ? ` · ${data.cached_used} from cache`
          : "";
        const overflowBits = data.over_cap > 0
          ? ` · click again to refresh the next ${data.over_cap}`
          : "";
        toast.success(
          `Refreshed ${data.updated} of ${data.attempted}${cacheBits}` +
          (data.skipped > 0 ? ` · ${data.skipped} skipped (no symbol / unsupported)` : "") +
          overflowBits
        );
        load();
      } else if (data.attempted === 0) {
        toast("No investments with a symbol to refresh. Add tickers first.");
      } else {
        toast.error(t("toasts.av_no_prices"));
      }
    } catch (e) {
      console.error("Refresh prices failed:", e);
      toast.error(e?.response?.data?.detail || "Could not refresh prices");
    } finally { setRefreshing(false); }
  };

  const cur = user?.currency || "USD";
  const invested = items.reduce((a,b) => a + Number(b.invested_amount||0), 0);
  const value = items.reduce((a,b) => a + Number(b.current_value||0), 0);
  const gain = value - invested;
  const gainPct = invested > 0 ? (gain / invested * 100) : 0;

  return (
    <div>
      <PageHeader
        subtitle={t("pages.investments.subtitle")}
        title={t("pages.investments.title")}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn-ghost"
              onClick={refreshPrices}
              disabled={refreshing}
              data-testid="refresh-prices-btn"
              title={t("pages.inv_body.refresh_tooltip")}
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {refreshing ? t("pages.inv_body.refreshing") : t("pages.inv_body.refresh_prices")}
            </button>
            <button className="btn-primary" onClick={startNew} data-testid="add-inv-btn"><Plus size={16}/> {t("pages.inv_body.add_investment")}</button>
          </div>
        }
      />

      <div className="grid md:grid-cols-4 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.inv_body.holdings")} value={items.length}/>
        <SummaryStat label={t("pages.inv_body.invested")} value={formatMoney(invested, cur)}/>
        <SummaryStat label={t("pages.inv_body.current_value")} value={formatMoney(value, cur)}/>
        <div className="card-surface p-5">
          <div className="label-eyebrow">{t("pages.inv_body.total_gain")}</div>
          <div className={`num-lg mt-2 ${gain >= 0 ? "text-[var(--accent-positive)]" : "text-[var(--accent-danger)]"}`}>
            {gain >= 0 ? "+" : ""}{formatMoney(gain, cur)} ({gainPct.toFixed(1)}%)
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.investments.empty_title")} body={t("pages.inv_body.empty_body")}
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.inv_body.add_investment")}</button>}/>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
              <tr><Th>{t("pages.inv_body.holding_h")}</Th><Th>{t("pages.inv_body.type_h")}</Th><Th>{t("pages.inv_body.invested_h")}</Th><Th>{t("pages.inv_body.value_h")}</Th><Th className="text-right">{t("pages.inv_body.return_h")}</Th><Th></Th></tr>
            </thead>
            <tbody>
              {items.map(i => {
                const g = i.current_value - i.invested_amount;
                const gp = i.invested_amount > 0 ? g/i.invested_amount*100 : 0;
                return (
                  <tr key={i.id} className="border-t border-[var(--border-default)] hover:bg-[var(--bg-secondary)]/60">
                    <Td>
                      <div className="font-medium">
                        {i.name}
                        {i.symbol && <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{i.symbol}</span>}
                      </div>
                      {i.quantity != null && <div className="text-xs text-[var(--text-muted)]">{t("pages.inv_body.qty_prefix")} {i.quantity}</div>}
                    </Td>
                    <Td><span className="badge badge-neutral">{i.type}</span></Td>
                    <Td className="num-md">{formatMoney(i.invested_amount, cur)}</Td>
                    <Td className="num-md">
                      {formatMoney(i.current_value, cur)}
                      {i.last_price_refreshed_at && (
                        <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 mt-0.5" data-testid={`inv-live-${i.id}`}>
                          <TrendingUp size={9} />
                          <span>{t("pages.inv_body.live_date", { date: new Date(i.last_price_refreshed_at).toLocaleDateString(undefined, { month:"short", day:"numeric" }) })}</span>
                        </div>
                      )}
                    </Td>
                    <Td className={`text-right num-md ${g >= 0 ? "text-[var(--accent-positive)]" : "text-[var(--accent-danger)]"}`}>{g >= 0 ? "+" : ""}{formatMoney(g, cur)} · {gp.toFixed(1)}%</Td>
                    <Td className="text-right">
                      <button className="btn-danger-ghost" onClick={() => startEdit(i)}><Pencil size={15}/></button>
                      <button className="btn-danger-ghost" onClick={() => remove(i.id)}><Trash2 size={15}/></button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("pages.inv_body.edit_title") : t("pages.inv_body.add_title")}>
        <form onSubmit={save} className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.inv_body.name_l")}><input required className="field-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-inv-name"/></Field>
            <Field label={t("pages.inv_body.type_l")}>
              <select className="field-input" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                {TYPES.map(x => <option key={x}>{x}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.inv_body.invested_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.invested_amount} onChange={e => setForm({...form, invested_amount: e.target.value})} data-testid="input-inv-invested"/></Field>
            <Field label={t("pages.inv_body.current_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.current_value} onChange={e => setForm({...form, current_value: e.target.value})} data-testid="input-inv-value"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.inv_body.quantity_opt")}><input type="number" step="0.0001" className="field-input" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})}/></Field>
            <Field label={t("pages.inv_body.date_l")}><DateField value={form.date} onChange={v => setForm({...form, date: v})} testid="input-inv-date"/></Field>
          </div>
          <Field label={t("pages.inv_body.symbol_l")}>
            <input
              className="field-input"
              value={form.symbol}
              onChange={e => setForm({...form, symbol: e.target.value})}
              placeholder={t("pages.inv_body.symbol_p")}
              maxLength={12}
              data-testid="input-inv-symbol"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-inv-btn">{edit ? t("common.save") : t("common.add")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
